import React from 'react';
import { XCircle, ArrowLeft, HelpCircle, Mail } from 'lucide-react';

interface PaymentCancelProps {
  onBack: () => void;
}

const PaymentCancel: React.FC<PaymentCancelProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-8">
        {/* Cancel Icon */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="absolute inset-0 bg-red-500 rounded-full blur-xl opacity-30"></div>
            <div className="relative bg-gradient-to-br from-red-500 to-rose-500 rounded-full p-5">
              <XCircle className="w-16 h-16 text-white" />
            </div>
          </div>
        </div>

        {/* Cancel Message */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
            Payment Cancelled
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Your payment was cancelled and you were not charged
          </p>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <HelpCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900 dark:text-blue-200 font-medium mb-1">
                Changed your mind?
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                You can upgrade to Pro anytime from your profile settings. All Pro features will be instantly available.
              </p>
            </div>
          </div>
        </div>

        {/* Free Plan Info */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-6">
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
            You're on the Free plan
          </p>
          <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
            <li>• Access to all templates</li>
            <li>• Copy and download prompts</li>
            <li>• Requires your own API keys</li>
            <li>• Cannot save prompts to cloud</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={onBack}
            className="w-full py-3 px-6 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-medium rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Continue with Free Plan
          </button>
          
          <button
            onClick={() => window.location.reload()}
            className="w-full py-3 px-6 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all"
          >
            Reconsider Pro Plan
          </button>
        </div>

        {/* Support */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-center text-gray-500 dark:text-gray-400 mb-3">
            Have questions about our plans?
          </p>
          <a
            href="mailto:support@fxprompt.app"
            className="flex items-center justify-center gap-2 text-sm text-cyan-600 hover:text-cyan-700"
          >
            <Mail className="w-4 h-4" />
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default PaymentCancel;